import { BLOCKS } from '@contentful/rich-text-types'
import {
  AssetOptionsContext,
  type AssetOptionsContextProps,
  DEFAULT_MAX_SVG_HEIGHT,
  DEFAULT_MIN_SVG_WIDTH,
} from '@contexts/asset-options'
import { PageContext } from '@contexts/page'
import { ContentType } from '@entries/content-types'
import ContentfulAsset from '@entries/contentful-asset'
import { withDisplayName } from '@hocs/with-display-name'
import LegalMentionsDialog from '@layouts/legal-mentions-dialog'
import { MainCta } from '@layouts/main-cta'
import { documentHeading1 } from '@render-node/document-heading-1'
import { children } from '@render-node/utils/children'
import { renderText } from '@render-text/index'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import { Divider } from '@ui/atoms/Divider'
import { EnhancedLink } from '@ui/enhanced-components/enhanced-components'
import { formatDate } from '@utils/date'
import React, { useContext, useState } from 'react'

const assetOptions: AssetOptionsContextProps = {
  width: '259px',
  height: 'auto',
  minSvgWidth: DEFAULT_MIN_SVG_WIDTH,
  maxSvgHeight: DEFAULT_MAX_SVG_HEIGHT,
  objectFit: 'contain',
  alt: 'logo',
}

const ContentfulOfferLanding: Utils.EntryFC<
  Queries.ContentfulOfferFragment
> = ({
  content: {
    title,
    promoNumber,
    promoSymbol,
    preBaseLine,
    baseLine,
    subtext,
    promoBeginning,
    promoEnd,
    promoCode,
    promoTextV2,
    topText,
    modalConditions,
  },
  entryInspectorProps,
  fieldInspectorProps,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { title: pageImageTitle } = useContext(PageContext)

  const close = () => {
    setIsOpen(false)
  }

  const open = () => {
    setIsOpen(true)
  }

  return (
    <div
      className="flex flex-col items-center p-5 pt-0 laptop:pt-5"
      {...entryInspectorProps()}
    >
      {title && (
        <>
          <RichText
            content={title}
            options={{
              ...defaultRichTextFieldOptionsDeprecated,
              renderNode: {
                ...defaultRichTextFieldOptionsDeprecated.renderNode,
                [BLOCKS.DOCUMENT]: documentHeading1,
                [BLOCKS.PARAGRAPH]: children,
              },
            }}
          />
          <Divider className="my-3 w-full" />
        </>
      )}
      {pageImageTitle?.image && (
        <div className="flex">
          <AssetOptionsContext.Provider value={assetOptions}>
            <Entry
              content={pageImageTitle?.image}
              mapping={{
                [ContentType.ContentfulAsset]: ContentfulAsset,
              }}
            />
          </AssetOptionsContext.Provider>
          <Divider className="my-3 w-full" />
        </div>
      )}

      {topText && (
        <h1 className="mt-5 text-center text-110 leading-alternate text-theme-default-accent1 laptop:text-135">
          {topText}
        </h1>
      )}
      <div className="text-center">
        <div className="mb-1 mt-4 flex justify-center text-theme-default-accent4 mix-blend-lighten">
          <div className="flex items-center rounded-md bg-theme-default-accent1 px-4 text-155 font-medium leading-alternate laptop:text-250">
            {preBaseLine && (
              <span
                {...fieldInspectorProps({
                  fieldId: 'preBaseLine',
                })}
                className="whitespace-nowrap"
              >
                {preBaseLine}&nbsp;
              </span>
            )}
            {promoNumber && (
              <span
                {...fieldInspectorProps({
                  fieldId: 'promoNumber',
                })}
              >
                {promoNumber}
              </span>
            )}
            {promoSymbol && (
              <span
                {...fieldInspectorProps({
                  fieldId: 'promoSymbol',
                })}
              >
                {promoSymbol}
              </span>
            )}
            {baseLine && (
              <span
                {...fieldInspectorProps({
                  fieldId: 'baseLine',
                })}
              >
                &nbsp;{baseLine}
              </span>
            )}
          </div>
        </div>
        {subtext && (
          <div
            className="mb-3 mt-1 text-110 font-medium leading-[1.5rem] text-theme-default-accent1 laptop:text-135 laptop:leading-[2.25rem]"
            {...fieldInspectorProps({
              fieldId: 'subtext',
            })}
          >
            {renderText(subtext)}
          </div>
        )}
        {promoTextV2 && (
          <div className="mt-3 text-theme-default-accent1">
            <RichText
              {...fieldInspectorProps({
                fieldId: 'promoTextV2',
              })}
              content={promoTextV2}
              options={defaultRichTextFieldOptionsDeprecated}
            />
          </div>
        )}
      </div>
      <div className="flex flex-col items-center justify-center laptop:items-start laptop:justify-start">
        <MainCta />
        <ul>
          {promoBeginning && promoEnd && (
            <li className="py-2 text-center">
              <p className="text-085 text-theme-default-accent1">
                Valable du{' '}
                <strong
                  {...fieldInspectorProps({
                    fieldId: 'promoBeginning',
                  })}
                >
                  {formatDate(promoBeginning)}
                </strong>{' '}
                au{' '}
                <strong
                  {...fieldInspectorProps({
                    fieldId: 'promoEnd',
                  })}
                >
                  {formatDate(promoEnd)}
                </strong>
              </p>
            </li>
          )}
          {promoCode && (
            <li className="py-2 text-center">
              <p className="text-085 text-theme-default-accent1">
                Avec le code opération :{' '}
                <span className="rounded-sm bg-theme-default-accent1 px-1 py-1 leading-[2rem] text-theme-default-accent4 mix-blend-lighten">
                  <strong
                    {...fieldInspectorProps({
                      fieldId: 'promoCode',
                    })}
                  >
                    {promoCode}
                  </strong>
                </span>
              </p>
            </li>
          )}
        </ul>
      </div>
      {modalConditions && (
        <div className="flex justify-center pt-1 laptop:justify-start">
          <EnhancedLink
            className="text-085 !text-theme-default-accent1 !underline !decoration-background-default"
            onClick={open}
          >
            Voir conditions
          </EnhancedLink>
          <LegalMentionsDialog
            legalMentions={modalConditions}
            visible={isOpen}
            onClose={close}
          />
        </div>
      )}
    </div>
  )
}

export default withDisplayName(ContentfulOfferLanding, 'ContentfulOfferLanding')
